// This file is auto-generated. DO NOT EDIT.

import {
  InternetDeviceSearchQuery,
  InternetDeviceWithRefs,
  InternetDeviceWithRefsProjection,
} from '../model/internet-device-api';
import { SelectManyResponse, MutationResponse, AggregateResponse } from './model';
import { ApiError } from './errors';
import { InternetDevice, InternetDeviceSortParams } from '../model/internet-device-model';
import { Account, AccountProjection } from '../model/account-model';
import { Group, GroupProjection } from '../model/group-model';
import {
  InternetDevicePairingCode,
  InternetDevicePairingCodeProjection,
} from '../model/internet-device-pairing-code-model';
import {
  InternetDeviceTelemetryDataUsage,
  InternetDeviceTelemetryDataUsageProjection,
} from '../model/internet-device-telemetry-data-usage-model';

export type SearchInternetDevicesParams = {
  baseUrl: string;
  query: InternetDeviceSearchQuery;
  sort?: InternetDeviceSortParams;
  projection?: InternetDeviceWithRefsProjection;
  limit?: number;
  skip?: number;
};

async function newApiError(response: Response, defaultText: string): Promise<ApiError> {
  let text = defaultText;
  try {
    text = await response.text();
  } catch (_) {}
  return new ApiError(text);
}

export function searchInternetDevices(
  params: SearchInternetDevicesParams,
): Promise<SelectManyResponse<InternetDeviceWithRefs>> {
  return fetch(`${params.baseUrl}/internet_devices/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: params.query,
      sort: params.sort,
      projection: params.projection,
      limit: params.limit,
      skip: params.skip,
    }),
  }).then(async (response) => {
    if (!response.ok) {
      const err = await newApiError(response, 'Failed to search InternetDevice');
      return Promise.reject(err);
    }
    return response.json();
  });
}

export type SelectInternetDeviceByIdParams = {
  baseUrl: string;
  id: string;
  projection?: InternetDeviceWithRefsProjection;
};

export function selectInternetDeviceById(
  params: SelectInternetDeviceByIdParams,
): Promise<InternetDeviceWithRefs> {
  return fetch(`${params.baseUrl}/internet_devices/id/${params.id}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      projection: params.projection,
    }),
  }).then(async (response) => {
    if (!response.ok) {
      const err = await newApiError(response, 'Failed to select InternetDevice');
      return Promise.reject(err);
    }
    return response.json();
  });
}

export type SelectInternetDeviceByIdentifierParams = {
  baseUrl: string;
  identifier: string;
  projection?: InternetDeviceWithRefsProjection;
};

export function selectInternetDeviceByIdentifier(
  params: SelectInternetDeviceByIdentifierParams,
): Promise<InternetDeviceWithRefs> {
  return fetch(`${params.baseUrl}/internet_devices/identifier/${params.identifier}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      projection: params.projection,
    }),
  }).then(async (response) => {
    if (!response.ok) {
      const err = await newApiError(response, 'Failed to select InternetDevice');
      return Promise.reject(err);
    }
    return response.json();
  });
}

export type SelectInternetDeviceByPublicIdParams = {
  baseUrl: string;
  publicId: string;
  projection?: InternetDeviceWithRefsProjection;
};

export function selectInternetDeviceByPublicId(
  params: SelectInternetDeviceByPublicIdParams,
): Promise<InternetDeviceWithRefs> {
  return fetch(`${params.baseUrl}/internet_devices/publicId/${params.publicId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      projection: params.projection,
    }),
  }).then(async (response) => {
    if (!response.ok) {
      const err = await newApiError(response, 'Failed to select InternetDevice');
      return Promise.reject(err);
    }
    return response.json();
  });
}

export type SaveInternetDeviceParams = {
  baseUrl: string;
  data: InternetDevice;
};

export function createInternetDevice(
  params: SaveInternetDeviceParams,
): Promise<MutationResponse<InternetDevice>> {
  return fetch(`${params.baseUrl}/internet_devices/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data: params.data }),
  }).then(async (response) => {
    if (!response.ok) {
      const err = await newApiError(response, 'Failed to create InternetDevice');
      return Promise.reject(err);
    }
    return response.json();
  });
}

export function updateInternetDevice(
  params: SaveInternetDeviceParams,
): Promise<MutationResponse<InternetDevice>> {
  return fetch(`${params.baseUrl}/internet_devices/update`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data: params.data }),
  }).then(async (response) => {
    if (!response.ok) {
      const err = await newApiError(response, 'Failed to update InternetDevice');
      return Promise.reject(err);
    }
    return response.json();
  });
}

export type DeleteInternetDeviceParams = {
  baseUrl: string;
  id: string;
};

export function deleteInternetDevice({ baseUrl, id }: DeleteInternetDeviceParams): Promise<void> {
  return fetch(`${baseUrl}/internet_devices/delete/${id}`, {
    method: 'DELETE',
  }).then(async (response) => {
    if (!response.ok) {
      const err = await newApiError(response, 'Failed to delete InternetDevice');
      return Promise.reject(err);
    }
    return;
  });
}

// Aggregation types
export type AggregateMethod = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'first' | 'last';

// Type-safe aggregatable fields
export const InternetDeviceAggregateFields = {
  BatteryLevel: 'batteryLevel',
  UsageBytes: 'usageBytes',
} as const;

export type InternetDeviceAggregateField =
  (typeof InternetDeviceAggregateFields)[keyof typeof InternetDeviceAggregateFields];

// Type-safe group-by fields
export const InternetDeviceGroupByFields = {
  AccountId: 'accountId',
  BatteryLevel: 'batteryLevel',
  Description: 'description',
  Dns1: 'dns1',
  Dns2: 'dns2',
  GroupId: 'groupId',
  HeartbeatReceivedAt: 'heartbeatReceivedAt',
  Identifier: 'identifier',
  IpSyncedAt: 'ipSyncedAt',
  IsEnabled: 'isEnabled',
  IsFactoryResetButtonEnabled: 'isFactoryResetButtonEnabled',
  Language: 'language',
  Name: 'name',
  PublicId: 'publicId',
  PublicName: 'publicName',
  Timezone: 'timezone',
  UsageBytes: 'usageBytes',
  UserPassword: 'userPassword',
} as const;

export type InternetDeviceGroupByField =
  (typeof InternetDeviceGroupByFields)[keyof typeof InternetDeviceGroupByFields];

export type AggregateFieldSpec = {
  field: InternetDeviceAggregateField;
  method: AggregateMethod;
  alias?: string;
};

// Aggregate result row with partial model fields and metadata
export type InternetDeviceAggregateResultRow = {
  // Group-by fields (original types)
  accountId?: string | null;
  batteryLevel?: number | null;
  description?: string | null;
  dns1?: string | null;
  dns2?: string | null;
  groupId?: string | null;
  heartbeatReceivedAt?: string | null;
  identifier?: string | null;
  ipSyncedAt?: string | null;
  isEnabled?: boolean | null;
  isFactoryResetButtonEnabled?: boolean | null;
  language?: string | null;
  name?: string | null;
  publicId?: string | null;
  publicName?: string | null;
  timezone?: string | null;
  usageBytes?: number | null;
  userPassword?: string | null;
  // Aggregate fields - always numbers since they're results of sum/avg/etc
  // Ref field account
  account?: Account | null;
  // Ref field group
  group?: Group | null;
  // Ref field internetDevicePairingCodes
  internetDevicePairingCodes?: InternetDevicePairingCode[] | null;
  // Ref field internetDeviceTelemetryDataUsages
  internetDeviceTelemetryDataUsages?: InternetDeviceTelemetryDataUsage[] | null;
  // Metadata indicating which fields are populated
  __groupKeys: InternetDeviceGroupByField[];
  __aggregateKeys: string[];
};

export type InternetDeviceAggregateResponse = {
  data: InternetDeviceAggregateResultRow[];
  total: number;
};

export type AggregateInternetDeviceParams = {
  baseUrl: string;
  query: InternetDeviceSearchQuery;
  fields: AggregateFieldSpec[];
  groupBy: InternetDeviceGroupByField[];
  internetDevicePairingCodesProjection?: InternetDevicePairingCodeProjection;
  internetDeviceTelemetryDataUsagesProjection?: InternetDeviceTelemetryDataUsageProjection;
  accountProjection?: AccountProjection;
  groupProjection?: GroupProjection;
};

export function aggregateInternetDevices(
  params: AggregateInternetDeviceParams,
): Promise<InternetDeviceAggregateResponse> {
  return fetch(`${params.baseUrl}/internet_devices/aggregate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: params.query,
      fields: params.fields,
      groupBy: params.groupBy,
      internetDevicePairingCodesProjection: params.internetDevicePairingCodesProjection,
      internetDeviceTelemetryDataUsagesProjection:
        params.internetDeviceTelemetryDataUsagesProjection,
      accountProjection: params.accountProjection,
      groupProjection: params.groupProjection,
    }),
  }).then(async (response) => {
    if (!response.ok) {
      const err = await newApiError(response, 'Failed to aggregate InternetDevice');
      return Promise.reject(err);
    }
    return response.json();
  });
}
